import ScrollReveal from "@/components/base/ScrollReveal";
import { motion } from "framer-motion";

const steps = [
  {
    emoji: "🌱",
    icon: "ri-seedling-line",
    title: "Ingredientes Selecionados",
    subtitle: "A origem de tudo",
    description:
      "Cada ingrediente é escolhido criteriosamente. Priorizamos fornecedores locais e produtos frescos de alta qualidade, garantindo que apenas o melhor chegue à nossa cozinha.",
    color: "primary" as const,
  },
  {
    emoji: "👨‍🍳",
    icon: "ri-user-star-line",
    title: "Produção Própria",
    subtitle: "Mãos que fazem a diferença",
    description:
      "Toda a produção acontece na nossa fábrica em Fortaleza. Processos artesanais combinados com padrões modernos de higiene, receitas exclusivas e uma equipe apaixonada.",
    color: "accent" as const,
  },
  {
    emoji: "🧪",
    icon: "ri-test-tube-line",
    title: "Controle de Qualidade",
    subtitle: "Excelência em cada detalhe",
    description:
      "Cada lote passa por inspeção rigorosa. Verificamos sabor, textura, aparência e temperatura — tudo para garantir que o produto final supere as expectativas.",
    color: "secondary" as const,
  },
  {
    emoji: "📦",
    icon: "ri-archive-line",
    title: "Embalagem",
    subtitle: "Proteção que preserva",
    description:
      "Embalagens desenvolvidas para manter o frescor, a temperatura ideal e a integridade dos alimentos. Sustentáveis, higiênicas e pensadas para facilitar o transporte.",
    color: "primary" as const,
  },
  {
    emoji: "🚚",
    icon: "ri-truck-line",
    title: "Entrega",
    subtitle: "Pontualidade que respeita você",
    description:
      "Logística própria com rotas otimizadas em Fortaleza. Seu pedido chega no prazo, na temperatura certa e com a mesma qualidade de quando saiu da nossa fábrica.",
    color: "accent" as const,
  },
  {
    emoji: "🥪",
    icon: "ri-restaurant-line",
    title: "Sua Mesa",
    subtitle: "O momento que importa",
    description:
      "Produtos frescos, saborosos e prontos para consumo. Seja no lanche do dia a dia, no evento corporativo ou na prateleira do seu mercado — a qualidade Big Mania na sua mesa.",
    color: "secondary" as const,
  },
];

const colorMap = {
  primary: {
    dot: "bg-primary-100 border-primary-300",
    icon: "text-primary-600",
    pill: "bg-primary-50 text-primary-700 border-primary-200/60",
  },
  accent: {
    dot: "bg-accent-100 border-accent-300",
    icon: "text-accent-600",
    pill: "bg-accent-50 text-accent-700 border-accent-200/60",
  },
  secondary: {
    dot: "bg-secondary-100 border-secondary-300",
    icon: "text-secondary-600",
    pill: "bg-secondary-50 text-secondary-700 border-secondary-200/60",
  },
};

export default function QualityTimeline() {
  return (
    <section
      id="qualidade"
      className="relative w-full py-20 md:py-28 bg-organic-warm overflow-hidden bg-leaf-subtle"
    >
      {/* Subtle grain overlay */}
      <div className="absolute inset-0 bg-grain-subtle pointer-events-none"></div>

      <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
        {/* Header */}
        <ScrollReveal className="text-center mb-16 md:mb-20">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-4">
            Nossa Trajetória
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-5">
            Do ingrediente à sua{" "}
            <span className="gradient-text-warm">mesa</span>
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-base md:text-lg leading-relaxed">
            Seis etapas que garantem a qualidade que você sente em cada mordida.
            Da seleção dos ingredientes até o momento especial na sua mesa.
          </p>
          {/* Decorative separator */}
          <div className="decorative-line-curve mt-6 pb-4"></div>
        </ScrollReveal>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line */}
          <div className="timeline-connector"></div>

          <div className="space-y-10 md:space-y-12">
            {steps.map((step, idx) => {
              const palette = colorMap[step.color];
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px 0px" }}
                  transition={{
                    duration: 0.6,
                    delay: idx * 0.08,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  className="relative flex items-start gap-5 md:gap-7 pl-0"
                >
                  {/* Timeline dot */}
                  <div
                    className={`relative z-10 w-14 h-14 md:w-16 md:h-16 rounded-full ${palette.dot} border-4 border-background-50 flex items-center justify-center shrink-0 shadow-[0_0_0_4px_oklch(var(--background-50))] transition-all duration-300 group-hover:scale-110`}
                  >
                    <span className="text-xl md:text-2xl leading-none">{step.emoji}</span>
                  </div>

                  {/* Card */}
                  <div className="flex-1 pt-0.5">
                    <div
                      className={`relative p-5 md:p-7 rounded-2xl bg-background-50/90 border border-background-200/40 card-glow transition-all duration-400 hover:-translate-y-0.5 cursor-default`}
                    >
                      {/* Step number pill */}
                      <span
                        className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wider mb-3 px-2.5 py-1 rounded-full border ${palette.pill}`}
                      >
                        Etapa {idx + 1}
                      </span>

                      <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-1">
                        {step.title}
                      </h3>
                      <p className="text-xs md:text-sm font-medium text-foreground-400 mb-3 tracking-wide">
                        {step.subtitle}
                      </p>
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom trust badges */}
        <ScrollReveal
          direction="up"
          delay={0.2}
          staggerChildren
          staggerDelay={0.1}
        >
          <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: "ri-shield-check-line", label: "Higiene" },
              { icon: "ri-award-line", label: "Qualidade" },
              { icon: "ri-building-2-line", label: "Fabricação Própria" },
              { icon: "ri-heart-pulse-line", label: "Segurança Alimentar" },
            ].map((badge, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -3 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center gap-2 p-5 rounded-xl bg-background-50/80 border border-background-200/40 text-center card-glow transition-all duration-300 cursor-default"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${badge.icon} text-primary-600 text-base`}></i>
                </div>
                <span className="text-xs md:text-sm font-semibold text-foreground-800">
                  {badge.label}
                </span>
              </motion.div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}